/* Presentation enhancement for this one product; purchasing stays with the existing configurator. */
(async () => {
  const root = document.querySelector('[data-ap201-reference]');
  if (!root || root.dataset.elitePcSummaryBound === 'true') return;
    if (window.ElitePCData?.pending) await window.ElitePCData.ready;
  if (window.ElitePCData?.yield) await window.ElitePCData.yield();
  root.dataset.elitePcSummaryBound = 'true';
  root.querySelector('[data-ap201-reviews]')?.addEventListener('click', event => {
    const reviewsTab = document.querySelector('[data-s3-tab="reviews"]');
    if (!reviewsTab) return;
    event.preventDefault();
    reviewsTab.click();
    reviewsTab.scrollIntoView({ behavior: 'smooth', block: 'center' });
    reviewsTab.focus({ preventScroll: true });
  });

  const title = root.querySelector('[data-ap201-build-title]');
  const config = root.querySelector('.a31-config');
  if (!title || !config) return;

  // The inline catalogue provides facts for the selected SKUs, not brand names.
  const options = new Map();
  try {
    const catalog = JSON.parse(root.querySelector('[data-elite-pc-catalog]')?.textContent || 'null')
      || window.ElitePCPrepared?.catalog?.(title.dataset.caseSku);
    for (const group of catalog?.componentGroups || []) {
      if (!['case', 'ram', 'sata_ssd', 'nvme_ssd', 'sata_hdd'].includes(group.key)) continue;
      for (const option of group.options || []) options.set(option.sku, { ...option, componentKey: group.key });
    }
  } catch { /* The selected trigger text still supplies memory and storage. */ }

  const trigger = key => root.querySelector(key === 'case' ? '.a31-case-trigger' : `[data-a31-component-key="${key}"]`);
  const selected = key => (trigger(key)?.dataset.eliteSelectedSku || '').split('|').filter(Boolean);
  const text = key => trigger(key)?.querySelector('.elite-pc-trigger__value')?.textContent.trim() || '';
  const spec = (option, pattern) => String(option?.displaySpecs?.find(item => pattern.test(item.label))?.value || '').trim();
  const capacity = value => {
    const match = String(value || '').replace(/,/g, '').match(/\b(\d+(?:\.\d+)?)\s*(GB|TB)\b/i);
    return match && Number(match[1]) > 0 ? `${Number(match[1])}${match[2].toUpperCase()}` : '';
  };

  function caseSummary() {
    const sku = selected('case')[0] || title.dataset.caseSku;
    const option = options.get(sku);
    const original = sku === title.dataset.caseSku;
    const colour = spec(option, /^(?:Case )?Colou?r$/i) || (original ? title.dataset.caseColour : '');
    const type = spec(option, /^Case Type$/i) || (original ? title.dataset.caseType : '') || 'Case';
    return `${colour} ${type}`.trim();
  }

  function memorySummary() {
    const sku = selected('memory')[0];
    if (!sku) return '';
    const option = options.get(sku);
    const total = spec(option, /^(?:RAM )?Total Capacity(?: \(GB\))?$/i);
    const memoryText = text('memory');
    const size = capacity(total) || (/^\d+(?:\.\d+)?$/.test(total) ? `${Number(total)}GB` : '') || capacity(memoryText);
    if (!size) return '';
    const generation = `${memoryText} ${spec(option, /^DDR Type$/i)}`.match(/\bDDR\s*(\d)\b/i);
    const speed = memoryText.match(/\b\d[\d,]*(?:\.\d+)?\s*(?:MT\/s|MHz)\b/i)?.[0] || '';
    return [size, generation ? `DDR${generation[1]}` : '', 'RAM', speed].filter(Boolean).join(' ');
  }

  function storageSummary(key, label) {
    const skus = selected(key);
    if (!skus.length) return '';
    let sizes = skus.map(sku => {
      const option = options.get(sku);
      return capacity(spec(option, /^(?:(?:SATA|NVMe) SSD |Storage )?Capacity$/i)) || capacity(option?.label);
    });
    if (sizes.some(size => !size)) sizes = (text(key).match(/\b\d[\d,]*(?:\.\d+)?\s*(?:GB|TB)\b/gi) || []).map(capacity);
    if (!sizes.length || sizes.some(size => !size)) return '';
    if (key === 'ssd') {
      const renderedDrives = text(key).split(/\s+\+\s+/);
      sizes = sizes.map((size, index) => {
        const component = options.get(skus[index])?.componentKey;
        const driveType = component === 'sata_ssd' ? 'SATA' : component === 'nvme_ssd' ? 'NVMe'
          : renderedDrives[index]?.match(/\b(SATA|NVMe)\b/i)?.[1];
        return `${size} ${driveType ? `${driveType} ` : ''}SSD`;
      });
    }
    const counts = new Map();
    for (const size of sizes) counts.set(size, (counts.get(size) || 0) + 1);
    return `${[...counts].map(([size, count]) => count > 1 ? `${count} × ${size}` : size).join(' + ')}${key === 'ssd' ? '' : ` ${label}`}`;
  }

  function componentSummary(key) {
    return selected(key).length ? text(key) : '';
  }

  function powerSummary() {
    const watts = componentSummary('psu').match(/\b(\d+(?:\.\d+)?)\s*(?:W|Watts?)\b/i);
    return watts ? `${Number(watts[1])}W PSU` : '';
  }

  function processorSummary() {
    const value = componentSummary('processor');
    return value.match(/\bRyzen\s+(?:[3579]\s+(?:PRO\s+)?[\w-]+|Threadripper\s+[\w-]+)/i)?.[0]
      || value.match(/\bCore\s+(?:Ultra\s+[3579]\s+[\w-]+|i[3579][-\s][\w-]+|[3579]\s+[\w-]+)/i)?.[0]
      || value.replace(/^(?:AMD|Intel)\s+/i, '').replace(/\s+Processor\b.*$/i, '').trim();
  }

  function graphicsSummary() {
    const value = componentSummary('graphics');
    return value.match(/\b(?:RTX|GTX)\s*\d{3,4}(?:\s*(?:Ti|SUPER)){0,2}\b|\bRX\s*\d{3,4}(?:\s*(?:XTX|XT|GRE)){0,2}\b|\bArc\s*[AB]\d{3}\b/i)?.[0]
      || value.replace(/\s*[—–|].*$/, '').trim();
  }

  function updateWarranty() {
    const value = text('warranty');
    if (!value || /loading/i.test(value)) return;
    const plan = value.match(/\b(Bronze|Silver|Gold|Platinum|Diamond)\b/i)?.[1];
    const years = value.match(/\b(\d+)\s*[-–]?\s*years?\b/i)?.[1];
    const heading = root.querySelector('[data-ap201-warranty-title]');
    const details = root.querySelector('[data-ap201-warranty-details]');
    const included = !selected('warranty').length;
    const name = plan ? `${plan} warranty${included ? ' included' : ''}` : value;
    const cover = years ? `${years} ${Number(years) === 1 ? 'year' : 'years'} – parts, labour & return` : 'Selected warranty cover';
    if (heading && heading.textContent !== name) heading.textContent = name;
    if (details && details.textContent !== cover) details.textContent = cover;
  }

  function updateTitle() {
    const caseName = caseSummary().replace(/\s*Case$/i, '').trim();
    const hardware = [processorSummary(), graphicsSummary()].filter(Boolean).join(' / ');
    const summary = `${caseName ? `${caseName} ` : ''}Gaming PC${hardware ? ` — ${hardware}` : ''}`;
    const specification = [memorySummary(), storageSummary('ssd', 'SSD'), storageSummary('hdd', 'HDD'), powerSummary()].filter(Boolean).join(' · ');
    const subtitle = root.querySelector('[data-ap201-build-specs]');
    if (title.textContent !== summary) title.textContent = summary;
    if (subtitle && subtitle.textContent !== specification) subtitle.textContent = specification;
    if (subtitle) subtitle.hidden = !specification;
    const accessibleName = `${summary}${specification ? ` — ${specification}` : ''} builder`;
    if (root.getAttribute('aria-label') !== accessibleName) root.setAttribute('aria-label', accessibleName);
    const tone = /\bwhite\b/i.test(caseSummary()) ? 'light' : 'white';
    if (root.dataset.ap201GalleryTone !== tone) root.dataset.ap201GalleryTone = tone;
    updateWarranty();
  }

  // Triggers represent the saved build, so cancelled picker drafts never rename it.
  new MutationObserver(updateTitle).observe(config, {
    childList: true, characterData: true, subtree: true,
    attributes: true, attributeFilter: ['data-elite-selected-sku']
  });
  updateTitle();
})();

/* Desktop presentation for this product only. The configurator owns selection and prices. */
(async () => {
  const root = document.querySelector('[data-ap201-reference]');
  if (!root || root.dataset.elitePcPresentationReady === 'true') return;
  const desktop = matchMedia('(min-width: 1024px)');
  const assetBase = document.currentScript?.src || '';
    if (window.ElitePCData?.pending) await window.ElitePCData.ready;
  if (window.ElitePCData?.yield) await window.ElitePCData.yield();

  const extra = 'data-ap201-picker-extra';
  const catalog = new Map();
  const groups = new Map();
  const gallery = new Map();
  const dialogs = new Map();
  const originalImages = new Map();
  const previewOptions = new WeakMap();
  const zoomBound = new WeakSet();
  let photoViewer;
  let accessoriesRead = false;
  let galleryRequest;
  let galleryPage = 0;

  function addCatalog(data) {
    for (const group of data?.componentGroups || []) {
      groups.set(group.key, group.options || []);
      for (const option of group.options || []) catalog.set(option.sku, option);
    }
  }
  try { addCatalog(JSON.parse(root.querySelector('[data-elite-pc-catalog]')?.textContent || 'null')
    || window.ElitePCPrepared?.catalog?.(root.querySelector('[data-ap201-build-title]')?.dataset.caseSku)); } catch { /* Cosmetic details can use the rendered option. */ }

  function readAccessories() {
    if (accessoriesRead) return;
    try {
      const cached = JSON.parse(sessionStorage.getItem('elite-pc-accessories:v5') || 'null');
      if (cached && Date.now() - cached.savedAt >= 0 && Date.now() - cached.savedAt < 300000) {
        addCatalog(cached.catalog);
        accessoriesRead = true;
      }
    } catch { /* Storage is optional; the existing picker remains usable. */ }
  }

  const paths = {
    monitor: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 22h8M12 17v5M5 14h14"/>',
    filter: '<path d="M4 7h16M4 17h16M9 4v6M15 14v6"/>',
    funnel: '<path d="M3 4h18l-7 8v7l-4 2v-9z"/>',
    stock: '<path d="m3 7 9-4 9 4-9 4zM3 12l9 4 9-4M3 17l9 4 9-4"/>',
    brand: '<path d="M3 3h8l10 10-8 8L3 11z"/><circle cx="7.5" cy="7.5" r="1"/>',
    controller: '<path d="M7 7h10c3 0 4 2 5 9 .4 3-2 4-4 1l-2-2H8l-2 2c-2 3-4.4 2-4-1 1-7 2-9 5-9zM7 9v5M4.5 11.5h5"/><circle cx="16" cy="10" r=".6"/><circle cx="19" cy="12.5" r=".6"/>',
    cpu: '<rect x="5" y="5" width="14" height="14" rx="2"/><rect x="9" y="9" width="6" height="6"/><path d="M8 2v3M12 2v3M16 2v3M8 19v3M12 19v3M16 19v3M2 8h3M2 12h3M2 16h3M19 8h3M19 12h3M19 16h3"/>',
    memory: '<rect x="2" y="6" width="20" height="11" rx="1"/><path d="M5 9h3v5H5zM10.5 9h3v5h-3zM16 9h3v5h-3zM5 17v3M9 17v3M15 17v3M19 17v3"/>',
    case: '<path d="m6 3 12-1 1 19-13 1zM9 3v18M12 6h3M12 9h3M12 18h3"/>',
    motherboard: '<rect x="4" y="2" width="16" height="20" rx="1"/><rect x="7" y="5" width="7" height="7"/><path d="M17 5v9M7 16h10M7 19h10"/>',
    graphics: '<rect x="2" y="5" width="19" height="13" rx="2"/><circle cx="8" cy="11.5" r="3.5"/><circle cx="16" cy="11.5" r="3.5"/><path d="M21 5h2v16M6 18v3h9v-3"/>',
    drive: '<path d="m5 3-3 17h20L19 3zM4 16h16"/><circle cx="12" cy="9" r="3"/><path d="M6 19h1M17 19h1"/>',
    cooler: '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="1"/><path d="M12 5c5 1 5 4 1 6M19 12c-1 5-4 5-6 1M12 19c-5-1-5-4-1-6M5 12c1-5 4-5 6-1"/>',
    power: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="m13 5-5 8h5l-2 6 6-9h-5z"/>',
    cable: '<path d="M8 3v5M13 3v5M6 8h9v3a4.5 4.5 0 0 1-9 0zM10.5 16v2a4 4 0 0 0 8 0v-3M16 12h5v3h-5z"/>',
    wifi: '<path d="M2 8a16 16 0 0 1 20 0M5 12a11 11 0 0 1 14 0M8 16a6 6 0 0 1 8 0"/><circle cx="12" cy="20" r="1"/>',
    connectivity: '<path d="M12 3v15M9 6l3-3 3 3M12 14l6-4V7M12 17l-6-4V9"/><circle cx="12" cy="20" r="2"/><circle cx="6" cy="7" r="2"/><path d="M16 3h4v4h-4z"/>',
    colour: '<path d="M12 3a9 9 0 1 0 0 18h2a2 2 0 0 0 0-4h-1a2 2 0 0 1 0-4h3c6 0 5-10-4-10z"/><circle cx="7" cy="9" r=".8"/><circle cx="11" cy="6" r=".8"/><circle cx="16" cy="7" r=".8"/>',
    shield: '<path d="m12 2 9 4v6c0 5-5 8-9 10-4-2-9-5-9-10V6zM7 12l3 3 7-7"/>',
    gift: '<path d="M3 9h18v4H3zM5 13v9h14v-9M12 9v13"/><path d="M12 9C3 9 5-1 10 4l2 5c9 0 7-10 2-5z"/>',
    shipping: '<path d="M2 5h12v13H2zM14 9h4l4 5v4h-8"/><circle cx="6" cy="19" r="2"/><circle cx="18" cy="19" r="2"/>',
    windows: '<path d="m2 5 9-1v8H2zM13 4l9-2v10h-9zM2 14h9v7l-9-1zM13 14h9v10l-9-2z"/>',
    search: '<circle cx="10" cy="10" r="6"/><path d="m15 15 6 6"/>',
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    back: '<path d="M19 12H5M11 6l-6 6 6 6"/>',
    chevron: '<path d="m6 15 6-6 6 6"/>',
    list: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 7h8M8 12h8M8 17h8"/>',
    trash: '<path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7"/>',
    star: '<path d="m12 2 3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/>',
    none: '<circle cx="12" cy="12" r="9"/><path d="m6 6 12 12"/>'
  };
  function iconKey(text) {
    const value = text.toLowerCase();
    if (/monitor/.test(value)) return 'monitor';
    for (const [pattern, key] of [[/controller|gamepad|^type$/, 'controller'], [/motherboard/, 'motherboard'], [/processor|cpu/, 'cpu'], [/graphics|gpu/, 'graphics'], [/memory|ram/, 'memory'], [/cooler|cooling/, 'cooler'], [/supply|watt/, 'power'], [/cable/, 'cable'], [/wi.fi|network/, 'wifi'], [/connect|interface|usb/, 'connectivity'], [/windows|operating/, 'windows'], [/warranty|protect/, 'shield'], [/shipping|delivery/, 'shipping'], [/accessor/, 'gift'], [/ssd|drive|storage|capacity/, 'drive'], [/case/, 'case'], [/brand|model|mpn/, 'brand'], [/colour/, 'colour'], [/stock/, 'stock']]) if (pattern.test(value)) return key;
    return 'filter';
  }
  function icon(key) {
    const node = document.createElement('span');
    node.className = 'ap201-picker-icon';
    node.setAttribute(extra, '');
    node.setAttribute('aria-hidden', 'true');
    node.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${paths[key] || paths.filter}</svg>`;
    return node;
  }
  function make(tag, className, text) {
    const node = document.createElement(tag);
    node.className = className;
    node.setAttribute(extra, '');
    if (text) node.textContent = text;
    return node;
  }
  function setText(node, text) { if (node.textContent !== text) node.textContent = text; }

  // Remove the old synthetic comparison labels. A PC total alone provides no
  // evidence of a previous selling price for that exact configuration.
  function decorateComparisonPrices(container) {
    container.querySelectorAll('.ap201-compare-price').forEach(node=>node.remove());
  }

  // Use the collection's public product photos, only when an accessory preview needs them.
  // The existing configurator remains the sole source of price and stock decisions.
  function loadAccessoryPhotos(option, schedule) {
    if (!option?.productId || gallery.has(option.sku) || galleryRequest || galleryPage >= 2) return;
    const runtime = root.querySelector('script[data-accessories-url]');
    if (!runtime?.dataset.accessoriesUrl) return;
    const url = new URL(runtime.dataset.accessoriesUrl, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/collections/')) return;
    url.pathname = `${url.pathname.replace(/\/$/, '')}/products.json`;
    url.search = new URLSearchParams({ limit: '250', page: String(galleryPage + 1) });
    galleryRequest = fetch(url, { signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(10000) : undefined }).then(response => {
      if (!response.ok) throw new Error('Photos unavailable');
      return response.json();
    }).then(data => {
      galleryPage += 1;
      for (const product of data.products || []) {
        const photos = (product.images || []).map(image => image.src).filter(Boolean);
        for (const variant of product.variants || []) if (variant.sku) gallery.set(variant.sku, photos);
      }
      if ((data.products || []).length < 250) galleryPage = 2;
    }).catch(async () => { galleryPage = 2; }).finally(async () => {
      galleryRequest = undefined;
      if (desktop.matches) schedule();
    });
  }
  function safePhoto(value, width) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || !/(?:^|\.)(?:shopify\.com|elitepcs\.co\.uk)$/.test(url.hostname)) return '';
      url.searchParams.set('width', String(width));
      return url.href;
    } catch { return ''; }
  }

  function photoKey(value) {
    const url = safePhoto(value, 600);
    // Shopify serves the same photo through both storefront and CDN URLs.
    return url ? new URL(url).pathname.split('/').pop() : '';
  }

  function zoomPhoto(sources, source, label, opener) {
    const urls = [];
    const seen = new Set();
    for (const candidate of [...sources, source]) {
      const url = safePhoto(candidate, 1200);
      const key = photoKey(url);
      if (url && !seen.has(key)) { seen.add(key); urls.push(url); }
    }
    if (!urls.length) return;
    photoViewer?.remove();
    const viewer = document.createElement('dialog');
    photoViewer = viewer;
    viewer.className = 'elite-pc-photo ap201-picker-photo';
    viewer.setAttribute('aria-label', `${label} image gallery`);
    const header = document.createElement('div'); header.className = 'elite-pc-photo__head';
    const title = document.createElement('h2'); title.textContent = label;
    const count = document.createElement('span'); count.className = 'ap201-gallery-count'; count.setAttribute('aria-live', 'polite'); count.setAttribute('aria-atomic', 'true');
    const close = document.createElement('button'); close.type = 'button'; close.setAttribute('aria-label', 'Close product photo');
    close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>';
    close.addEventListener('click', () => viewer.close());
    const stage = document.createElement('div'); stage.className = 'ap201-gallery-stage';
    const image = document.createElement('img'); image.className = 'elite-pc-photo__image'; image.loading = 'eager';
    const previous = document.createElement('button'); previous.type = 'button'; previous.setAttribute('aria-label', 'Previous product photo'); previous.append(icon('back'));
    const next = document.createElement('button'); next.type = 'button'; next.setAttribute('aria-label', 'Next product photo'); next.append(icon('arrow'));
    previous.hidden = next.hidden = urls.length < 2;
    const thumbs = document.createElement('div'); thumbs.className = 'ap201-gallery-thumbs'; thumbs.setAttribute('role', 'group'); thumbs.setAttribute('aria-label', 'Gallery photos'); thumbs.hidden = urls.length < 2;
    let index = Math.max(0, urls.findIndex(url => photoKey(url) === photoKey(source)));
    const show = value => {
      index = (value + urls.length) % urls.length;
      image.alt = `${label} — photo ${index + 1} of ${urls.length}`;
      image.src = urls[index];
      count.textContent = `${index + 1} / ${urls.length}`;
      count.setAttribute('aria-label', `Image ${index + 1} of ${urls.length}`);
      [...thumbs.children].forEach((button, position) => button.setAttribute('aria-pressed', String(position === index)));
      if (viewer.open) thumbs.children[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    };
    urls.forEach((url, position) => {
      const thumb = document.createElement('button'); thumb.type = 'button'; thumb.setAttribute('aria-label', `View gallery photo ${position + 1}`);
      const thumbnail = document.createElement('img'); thumbnail.src = safePhoto(url, 160); thumbnail.alt = ''; thumbnail.loading = 'lazy';
      thumb.append(thumbnail); thumb.addEventListener('click', () => show(position)); thumbs.append(thumb);
    });
    previous.addEventListener('click', () => show(index - 1));
    next.addEventListener('click', () => show(index + 1));
    viewer.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault(); show(index + (event.key === 'ArrowLeft' ? -1 : 1));
    });
    viewer.addEventListener('click', event => { if (event.target === viewer) viewer.close(); });
    stage.append(previous, image, next);
    header.append(title, count, close); viewer.append(header, stage, thumbs);
    viewer.addEventListener('close', () => { if (opener.isConnected) opener.focus({ preventScroll: true }); viewer.remove(); });
    show(index);
    document.body.append(viewer); viewer.showModal(); close.focus();
  }

  function decorateHeader(dialog) {
    const header = dialog.querySelector('.elite-pc-picker__head, .elite-shipping-picker__head');
    const heading = header?.querySelector('h2');
    if (!header || !heading) return;
    if (!header.querySelector('.ap201-picker-brand')) {
      const brand = make('div', 'ap201-picker-brand');
      brand.setAttribute('aria-label', 'ElitePCS — Built for a higher standard');
      const logo = make('img', 'ap201-picker-logo');
      logo.src = document.querySelector('.elite-header__logo-image')?.src || new URL('elitepcs-logo-white.webp', assetBase).href;
      logo.alt = 'ELITEPCS';
      logo.width = 1229; logo.height = 198;
      logo.decoding = 'async';
      brand.append(logo, make('small', '', 'BUILT FOR A HIGHER STANDARD'));
      header.prepend(brand);
    }
    const titleIcon = heading.previousElementSibling;
    const key = iconKey(heading.textContent);
    if (!titleIcon?.classList.contains('ap201-picker-icon') || titleIcon.dataset.icon !== key) {
      if (titleIcon?.classList.contains('ap201-picker-icon')) titleIcon.remove();
      const mark = icon(key); mark.dataset.icon = key;
      heading.before(mark);
    }
  }

  function decorateFilters(dialog) {
    const panel = dialog.querySelector('.elite-pc-picker__filter-panel');
    if (!panel) return;
    const title = panel.querySelector('p.elite-pc-picker__filter-title');
    if (title && !title.querySelector('.ap201-picker-icon')) title.prepend(icon('filter'));
    const summary = panel.querySelector('.elite-pc-picker__filter-summary');
    if (summary && !summary.querySelector('.ap201-picker-reset')) {
      const reset = make('button', 'ap201-picker-reset', 'Reset');
      reset.type = 'button';
      reset.addEventListener('click', () => dialog.querySelector('[data-filter-reset]')?.click());
      summary.append(reset);
    }
    for (const field of panel.querySelectorAll('.elite-pc-picker__filter')) {
      const legend = field.querySelector('legend');
      if (!legend || legend.querySelector('.ap201-filter-collapse')) continue;
      const label = legend.textContent;
      legend.prepend(icon(iconKey(label)));
      const toggle = make('button', 'ap201-filter-collapse');
      toggle.type = 'button';
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', `Collapse ${label} filters`);
      toggle.append(icon('chevron'));
      toggle.addEventListener('click', () => {
        const collapsed = field.classList.toggle('ap201-filter-collapsed');
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} ${label} filters`);
      });
      legend.append(toggle);
    }
    const clear = dialog.querySelector('.elite-pc-picker__categories [data-filter-reset]');
    if (clear && !clear.querySelector('.ap201-picker-icon')) clear.prepend(icon('funnel'));
    decorateNumericFilters(panel);
    decorateFilterCounts(dialog, panel);
  }

  function decorateNumericFilters(panel) {
    for (const field of panel.querySelectorAll('[data-filter-kind="range"]')) {
      const min = field.querySelector('input[data-filter-range="min"]');
      const max = field.querySelector('input[data-filter-range="max"]');
      let values;
      try { values = JSON.parse(field.dataset.ap201NumericChoices || 'null'); } catch { continue; }
      if (!min || !max || !Array.isArray(values) || !values.length || !values.every(Number.isFinite)) continue;
      let controls = field.querySelector('.ap201-numeric-options');
      const sync = () => {
        const all = min.value === min.min && max.value === max.max;
        const buttons = [...controls.querySelectorAll('[data-ap201-numeric-value]')];
        let hidden = 0;
        buttons.forEach((button, index) => {
          const value = button.dataset.ap201NumericValue;
          const selected = !all && Number(value) >= Number(min.value) && Number(value) <= Number(max.value);
          if (button.getAttribute('aria-pressed') !== String(selected)) button.setAttribute('aria-pressed', String(selected));
          const hide = index >= 8 && controls.dataset.expanded !== 'true' && !selected;
          if (button.hidden !== hide) button.hidden = hide;
          if (button.hidden) hidden++;
        });
        const more = controls.querySelector('.ap201-numeric-more');
        if (more) {
          more.hidden = controls.dataset.expanded !== 'true' && hidden === 0;
          more.setAttribute('aria-expanded', String(controls.dataset.expanded === 'true'));
          setText(more, controls.dataset.expanded === 'true' ? 'Show less' : `Show ${hidden} more`);
        }
      };
      if (!controls) {
        controls = make('div', 'ap201-numeric-options');
        const label = field.dataset.filterField;
        const unit = field.dataset.ap201NumericUnit || '';
        const format = value => {
          const terabytes = unit === 'GB' && value >= 1000 && value % 1000 === 0;
          const amount = (terabytes ? value / 1000 : value).toLocaleString('en-GB', { maximumFractionDigits: 3 });
          return `${amount}${unit ? ` ${terabytes ? 'TB' : unit}` : ''}`;
        };
        controls.setAttribute('role', 'group');
        controls.setAttribute('aria-label', `${label} options`);
        for (const value of values) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = format(value);
          button.dataset.ap201NumericValue = String(value);
          button.setAttribute('aria-label', `Filter by ${label}: ${format(value)}`);
          button.addEventListener('click', () => {
            // Use the existing filter engine for exact values, Reset and mobile continuity.
            const clear = Number(min.value) === value && Number(max.value) === value;
            min.value = clear ? min.min : String(value);
            max.value = clear ? max.max : String(value);
            max.dispatchEvent(new Event('input', { bubbles: true }));
            sync();
          });
          controls.append(button);
        }
        if (values.length > 8) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'ap201-numeric-more';
          more.addEventListener('click', () => {
            controls.dataset.expanded = String(controls.dataset.expanded !== 'true');
            sync();
          });
          controls.append(more);
        }
        field.append(controls);
      }
      sync();
    }
  }

  function decorateFilterCounts(dialog, panel) {
    let keys;
    if (dialog.dataset.pickerMode === 'accessories') {
      const active = [...dialog.querySelectorAll('[data-accessory-category][aria-pressed="true"]')].map(button => button.dataset.accessoryCategory);
      keys = active.length ? active : [...dialog.querySelectorAll('[data-accessory-category]')].map(button => button.dataset.accessoryCategory);
    } else {
      const active = dialog.dataset.componentKey || root.querySelector('[data-a31-component-key][aria-expanded="true"]')?.dataset.a31ComponentKey || (root.querySelector('.a31-case-trigger[aria-expanded="true"]') ? 'case' : '');
      keys = ({ memory: ['ram'], ssd: ['sata_ssd', 'nvme_ssd'], hdd: ['sata_hdd'], operating_system: ['windows_key'], power_cable: ['kettle_lead'] })[active] || [active];
    }
    const options = keys.flatMap(key => groups.get(key) || []);
    if (!options.length) return;
    const rangeChanged = [...panel.querySelectorAll('input[type="range"]')].some(input => input.value !== (input.dataset.filterRange === 'max' ? input.max : input.min));
    if (rangeChanged) { panel.querySelectorAll('.ap201-filter-count').forEach(node => node.remove()); return; }
    const stock = option => option.availability?.available === true && option.availability?.quantity !== 0;
    const matches = (option, field, value) => (option.displaySpecs || []).some(spec => spec.label === field && String(spec.value).split(/[,;|]/).map(part => part.trim().toLowerCase()).includes(value.trim().toLowerCase()));
    const fields = [...panel.querySelectorAll('[data-filter-field]')];
    const stockValues = [...panel.querySelectorAll('[data-stock-filter]:checked')].map(input => input.dataset.stockFilter);
    const criteria = fields.map(field => ({
      label: field.dataset.filterField,
      values: [...field.querySelectorAll('input[data-filter-value]:checked, button[data-filter-value][aria-pressed="true"]')].map(input => input.dataset.filterValue)
    })).filter(field => field.values.length);
    const supports = label => options.every(option => (option.displaySpecs || []).some(spec => spec.label === label));
    // Some component catalogues supply only capacity; do not invent brand/spec counts.
    if (criteria.some(field => !supports(field.label))) {
      panel.querySelectorAll('.ap201-filter-count').forEach(node => node.remove());
      return;
    }
    const filtered = (option, except) => {
      if (except !== 'Stock' && stockValues.length && !stockValues.includes(stock(option) ? 'in' : 'out')) return false;
      return criteria.every(field => field.label === except || field.values.some(value => matches(option, field.label, value)));
    };
    const candidates = new Map();
    for (const input of panel.querySelectorAll('.elite-pc-picker__filter-choice input')) {
      if (input.closest('.elite-pc-picker__additional-filters, [hidden]')) continue;
      const field = input.closest('[data-filter-field]')?.dataset.filterField || 'Stock';
      if (field !== 'Stock' && !supports(field)) {
        input.parentElement.querySelector('.ap201-filter-count')?.remove();
        continue;
      }
      if (!candidates.has(field)) candidates.set(field, options.filter(option => filtered(option, field)));
      let count = input.parentElement.querySelector('.ap201-filter-count');
      if (!count) { count = make('small', 'ap201-filter-count'); count.setAttribute('aria-hidden', 'true'); input.parentElement.append(count); }
      const total = candidates.get(field).filter(option => input.dataset.stockFilter ? stock(option) === (input.dataset.stockFilter === 'in') : matches(option, field, input.dataset.filterValue || input.value)).length;
      setText(count, String(total));
    }
  }

  function decorateCards(dialog) {
    for (const card of dialog.querySelectorAll('.elite-pc-picker__option')) {
      if (card.classList.contains('elite-pc-picker__none')) {
        const detail = card.querySelector('.elite-pc-picker__detail');
        if (detail && !detail.querySelector('.ap201-none-caption')) {
          const item = detail.querySelector('strong')?.textContent.replace(/^No /i, '') || 'this component';
          detail.append(make('small', 'ap201-none-caption', `Continue without ${item}.`));
          card.prepend(icon('none'));
        }
      }
      for (const line of card.querySelectorAll('.elite-pc-picker__detail li:not(.elite-pc-picker__extra-spec)')) {
        if (!line.querySelector('.ap201-picker-icon')) line.prepend(icon(iconKey(line.textContent.split(':')[0])));
      }
    }
    const main = dialog.querySelector('.elite-pc-picker__main');
    const results = main?.querySelector('.elite-pc-picker__results');
    if (main && results?.dataset.matchCount !== undefined) {
      let count = main.querySelector('.ap201-picker-result-count');
      if (!count) { count = make('span', 'ap201-picker-result-count'); main.querySelector('h3')?.after(count); }
      const value = Number(results.dataset.matchCount);
      setText(count, `${value} result${value === 1 ? '' : 's'}`);
    }
  }

  function decoratePreview(dialog, schedule) {
    const preview = dialog.querySelector('.elite-pc-picker__preview');
    const photo = preview?.querySelector('.elite-pc-picker__preview-image > img');
    const heading = preview?.querySelector('h4');
    if (!preview || !heading) return;
    const imageId = photo?.dataset.pcImageId;
    const selected = [...dialog.querySelectorAll('.elite-pc-picker__option[data-sku]')].find(button => button.querySelector('.elite-pc-picker__detail strong')?.textContent === heading.textContent);
    const remembered = photo && previewOptions.get(photo);
    // Search, stock filters and pagination can hide the selected option card.
    // Its image still identifies the preview when the desktop layout rebuilds.
    const option = catalog.get(selected?.dataset.sku || remembered?.sku) || remembered
      || [...catalog.values()].find(item => item.label?.replace(/\s+included$/i, '').trim() === heading.textContent.trim());
    if (photo && option) previewOptions.set(photo, option);
    const specs = preview.querySelector('dl');
    const brand = [...(specs?.querySelectorAll('div') || [])].find(row => row.querySelector('dt')?.textContent === 'Brand')?.querySelector('dd')?.textContent;
    if (brand && !preview.querySelector('.ap201-preview-brand')) {
      const mark = make('div', 'ap201-preview-brand', brand);
      if (/^ASUS$/i.test(brand) && assetBase) {
        mark.textContent = '';
        const logo = document.createElement('img');
        logo.src = new URL('elite-ap201-asus-logo.svg', assetBase).href;
        logo.alt = 'ASUS'; logo.width = 124; logo.height = 27;
        mark.append(logo);
      }
      preview.prepend(mark);
    }
    for (const term of specs?.querySelectorAll('dt') || []) if (!term.querySelector('.ap201-picker-icon')) term.prepend(icon(iconKey(term.textContent)));
    if (photo) {
      const sources = [...(gallery.get(option?.sku) || []), ...(option?.images || []), originalImages.get(photo) || photo.src];
      // The current product gallery provides the published case photos as well.
      if (option?.sku === root.querySelector('.a31-case-trigger')?.dataset.eliteSelectedSku && /case/i.test(dialog.querySelector('.elite-pc-picker__head h2')?.textContent || '')) {
        for (const image of root.querySelectorAll('.a31-thumb img')) sources.push(image.src);
      }
      const urls = [];
      const seen = new Set();
      for (const source of sources) {
        const url = safePhoto(source, 600);
        const key = photoKey(url);
        if (url && !seen.has(key)) { seen.add(key); urls.push(url); }
      }
      const signature = JSON.stringify([imageId, urls]);
      const existingThumbs = preview.querySelector('.ap201-preview-thumbs');
      if (urls.length < 2) existingThumbs?.remove();
      // Public product photos can arrive after the first preview has rendered.
      // Refresh the strip without resetting the photo the customer is viewing.
      if (urls.length > 1 && existingThumbs?.dataset.gallerySignature !== signature) {
        existingThumbs?.remove();
        const thumbs = make('div', 'ap201-preview-thumbs');
        thumbs.dataset.imageId = imageId || '';
        thumbs.dataset.gallerySignature = signature;
        thumbs.setAttribute('aria-label', 'Product photos');
        if (!originalImages.has(photo)) originalImages.set(photo, photo.src);
        const activeKey = photoKey(photo.src);
        urls.forEach((url, index) => {
          const thumb = document.createElement('button');
          thumb.type = 'button'; thumb.setAttribute('aria-label', `View product photo ${index + 1}`);
          thumb.setAttribute('aria-pressed', String(photoKey(url) === activeKey));
          const image = document.createElement('img');
          image.src = safePhoto(url, 120); image.alt = ''; image.width = 72; image.height = 64; image.loading = 'lazy';
          thumb.append(image);
          thumb.addEventListener('click', () => {
            photo.src = url;
            photo.removeAttribute('srcset');
            thumbs.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button === thumb)));
          });
          thumbs.append(thumb);
        });
        photo.parentElement.after(thumbs);
        if (!zoomBound.has(photo.parentElement)) {
          const opener = photo.parentElement;
          zoomBound.add(opener);
          opener.addEventListener('click', event => {
            if (!desktop.matches || !preview.querySelector('.ap201-preview-thumbs')) return;
            event.stopImmediatePropagation();
            const sources = [...preview.querySelectorAll('.ap201-preview-thumbs img')].map(image => image.src);
            zoomPhoto(sources, photo.src, heading.textContent, opener);
          }, { capture: true });
        }
      }
    }
    if (dialog.dataset.pickerMode === 'accessories' || dialog.dataset.componentKey === 'monitors') loadAccessoryPhotos(option, schedule);
  }

  function decorateFooter(dialog) {
    const accessories = dialog.querySelector('.elite-pc-picker__accessory-selection:not([hidden])');
    if (accessories) {
      const title = accessories.querySelector('strong');
      if (title && !title.querySelector('.ap201-picker-icon')) title.prepend(icon('list'));
      const chips = accessories.querySelector('.elite-pc-picker__accessory-chips');
      if (chips && !accessories.querySelector('.ap201-accessories-clear')) {
        const clear = make('button', 'ap201-accessories-clear', 'Remove all');
        clear.type = 'button'; clear.prepend(icon('trash'));
        clear.addEventListener('click', () => {
          for (const remove of [...dialog.querySelectorAll('[data-accessory-remove]')]) remove.click();
        });
        accessories.prepend(clear);
      }
      for (const chip of accessories.querySelectorAll('.elite-pc-picker__accessory-chip')) {
        if (chip.querySelector('.ap201-accessory-photo')) continue;
        const option = catalog.get(chip.dataset.accessorySku);
        const source = dialog.querySelector(`.elite-pc-picker__option[data-sku="${CSS.escape(chip.dataset.accessorySku)}"] img`)?.src || option?.images?.[0];
        const url = safePhoto(source, 80);
        if (url) {
          const image = make('img', 'ap201-accessory-photo'); image.src = url; image.alt = ''; image.width = 42; image.height = 36;
          chip.querySelector('button')?.prepend(image);
        }
      }
    }
    const total = dialog.querySelector('.elite-pc-picker__total');
    if (total && !total.querySelector('.ap201-picker-icon')) total.prepend(icon('stock'));
    const shippingTotal = dialog.querySelector('.elite-shipping-picker__total');
    if (shippingTotal && !shippingTotal.querySelector('.ap201-shipping-price-label')) {
      const label = make('span', 'ap201-shipping-price-label', 'Full PC price');
      label.prepend(icon('stock'));
      shippingTotal.prepend(label);
      shippingTotal.append(make('span', 'ap201-shipping-price-caption', 'With these selections'));
    }
    const back = dialog.querySelector('.elite-pc-picker__actions [data-picker-back], .elite-shipping-picker__actions [data-shipping-previous]');
    if (back && !back.querySelector('.ap201-picker-icon')) back.prepend(icon('back'));
    const next = dialog.querySelector('[data-picker-next], [data-shipping-next]');
    if (next && !next.querySelector('.ap201-picker-icon')) next.append(icon('arrow'));
    const save = dialog.querySelector('[data-picker-save], [data-shipping-save]');
    if (save) {
      save.dataset.ap201SaveLabel ??= save.textContent;
      setText(save, 'Save and close');
    }
  }

  function attach(dialog) {
    if (dialogs.has(dialog)) return;
    dialog.classList.add('ap201-desktop-picker');
    const categoryBar = dialog.querySelector('.elite-pc-picker__category-bar');
    const categoryParent = categoryBar?.parentNode;
    const categoryNext = categoryBar?.nextSibling;
    let categoryToReveal = '';
    function restoreCategoryBar() {
      if (!categoryBar) return;
      if (categoryBar.parentNode !== categoryParent) categoryParent.insertBefore(categoryBar, categoryNext?.parentNode === categoryParent ? categoryNext : null);
      categoryBar.classList.remove('ap201-sidebar-categories');
    }
    function placeCategoryBar() {
      if (!categoryBar) return;
      const summary = dialog.querySelector('.elite-pc-picker__filter-summary');
      if (dialog.dataset.pickerMode !== 'accessories' || !summary) { restoreCategoryBar(); return; }
      // Keep the original category buttons and their handlers through filter repainting.
      if (categoryBar.previousElementSibling !== summary) summary.after(categoryBar);
      categoryBar.classList.add('ap201-sidebar-categories');
      if (!categoryBar.querySelector('.ap201-category-heading')) {
        const heading = make('h3', 'ap201-category-heading', 'Category');
        heading.prepend(icon('gift'));
        categoryBar.prepend(heading);
      }
    }
    function revealSidebarCategory(event) {
      if (!desktop.matches || !categoryBar?.classList.contains('ap201-sidebar-categories') || !event.target.closest('[data-accessory-category]')) return;
      categoryToReveal = event.target.closest('[data-accessory-category]').dataset.accessoryCategory;
      schedule();
    }
    categoryBar?.addEventListener('click', revealSidebarCategory);
    let frame = 0;
    const observer = new MutationObserver(schedule);
    const watch = () => observer.observe(dialog, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['open', 'aria-pressed', 'data-match-count', 'data-filter-count', 'disabled', 'src'] });
    function render() {
      cancelAnimationFrame(frame);
      frame = 0;
      if (!desktop.matches) return;
      observer.disconnect();
      dialog.classList.add('ap201-desktop-picker');
      if (dialog.dataset.pickerMode === 'accessories' || dialog.dataset.componentKey === 'monitors') readAccessories();
      placeCategoryBar();
      decorateHeader(dialog);
      decorateFilters(dialog);
      decorateCards(dialog);
      decoratePreview(dialog, schedule);
      decorateFooter(dialog);
      decorateComparisonPrices(dialog);
      if (categoryToReveal) {
        const selected = categoryBar.querySelector(`[data-accessory-category="${categoryToReveal}"]`);
        selected?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        selected?.focus({ preventScroll: true });
        categoryToReveal = '';
      }
      watch();
    }
    function schedule() {
      if (frame || !desktop.matches) return;
      frame = requestAnimationFrame(async () => {
        frame = 0;
        if (!desktop.matches || !dialog.open) return;
        render();
      });
    }
    dialogs.set(dialog, { schedule, render, observer, restoreCategoryBar, categoryBar, revealSidebarCategory });
    dialog.addEventListener('elite-pc-picker:render', render);
    watch(); schedule();
  }
  function scan() {
    decorateComparisonPrices(root);
    if (!desktop.matches) return;
    for (const dialog of document.querySelectorAll('.elite-pc-picker, .elite-shipping-picker')) attach(dialog);
  }
  desktop.addEventListener('change', () => {
    decorateComparisonPrices(root);
    if (desktop.matches) { scan(); for (const state of dialogs.values()) state.schedule(); return; }
    for (const [dialog, state] of dialogs) {
      state.observer.disconnect();
      dialog.removeEventListener('elite-pc-picker:render', state.render);
      state.categoryBar?.removeEventListener('click', state.revealSidebarCategory);
      state.restoreCategoryBar();
      dialog.classList.remove('ap201-desktop-picker');
      dialog.querySelectorAll(`[${extra}]`).forEach(node => node.remove());
      for (const save of dialog.querySelectorAll('[data-ap201-save-label]')) {
        setText(save, save.dataset.ap201SaveLabel);
        delete save.dataset.ap201SaveLabel;
      }
      dialog.querySelectorAll('.ap201-filter-collapsed').forEach(node => node.classList.remove('ap201-filter-collapsed'));
    }
    dialogs.clear();
    for (const [photo, source] of originalImages) if (photo.isConnected) photo.src = source;
    originalImages.clear();
    photoViewer?.close(); photoViewer?.remove();
  });
  new MutationObserver(scan).observe(document.body, { childList: true });
  const pagePrice = root.querySelector('.a31-price');
  if (pagePrice) new MutationObserver(() => decorateComparisonPrices(root)).observe(pagePrice, { childList: true, characterData: true, subtree: true });
  scan();
  root.dataset.elitePcPresentationReady = 'true';
  document.dispatchEvent(new CustomEvent('elite-pc:prepared'));
})();
